/**
 * Player Name Edit — /(app)/settings/account/name
 *
 * Debounced uniqueness check against /auth/check-username; server
 * NAME_TAKEN surfaced as inline error. Save → PATCH /api/v1/users/me.
 *
 * Player Name == @username: both are the same value. Editing here
 * posts to PATCH /api/v1/users/me { displayName } and the server
 * updates username in the same transaction.
 */

import { COMPACT_HEADER_STYLE } from '../../../../lib/gameChrome'
import React, { useEffect, useState } from 'react'
import {
  View,
  Text,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useOrientation } from '../../../../hooks/useOrientation'
import { useAuth } from '../../../../hooks/useAuth'
import { apiGet, apiPatch, HttpError } from '../../../../lib/api'
import { Button } from '../../../../components/ui/Button'
import { Input } from '../../../../components/ui/Input'
import { KeyboardAwareScrollView } from '../../../../components/ui/KeyboardAwareScrollView'
import { useToast } from '../../../../components/ui/Toast'
import type { UserResponse } from '@warcarp/api-types'
import { goBackOr } from '../../../../lib/goBack'

// Mirror the same constraints used on the register screen and the API.
const USERNAME_MIN = 3
const USERNAME_MAX = 20
const USERNAME_RE = /^[a-zA-Z0-9_]+$/
const DEBOUNCE_MS = 400

type NameStatus = 'idle' | 'invalid' | 'checking' | 'available' | 'taken' | 'unchanged' | 'error'

export default function PlayerNameEditScreen() {
  const { user, refreshUser } = useAuth()
  const { showToast } = useToast()
  const router = useRouter()
  const { width: winWidth } = useWindowDimensions()
  const { isLandscape } = useOrientation()
  const contentWidth = isLandscape ? Math.min(480, winWidth * 0.55) : undefined

  const [playerName, setPlayerName] = useState(user?.displayName ?? '')
  const [playerNameError, setPlayerNameError] = useState('')
  const [nameStatus, setNameStatus] = useState<NameStatus>('idle')
  const [saving, setSaving] = useState(false)

  const nameChanged = playerName.trim() !== (user?.displayName ?? '')

  useEffect(() => {
    const trimmed = playerName.trim()

    if (!trimmed) {
      setNameStatus('idle')
      setPlayerNameError('')
      return
    }

    if (trimmed === (user?.displayName ?? '')) {
      setNameStatus('unchanged')
      setPlayerNameError('')
      return
    }

    if (
      trimmed.length < USERNAME_MIN ||
      trimmed.length > USERNAME_MAX ||
      !USERNAME_RE.test(trimmed)
    ) {
      setNameStatus('invalid')
      return
    }

    setNameStatus('checking')
    const handle = setTimeout(async () => {
      try {
        const res = await apiGet<{ available: boolean }>(
          `/api/v1/auth/check-username?username=${encodeURIComponent(trimmed)}`,
        )
        setPlayerName((curr) => {
          if (curr.trim() === trimmed) {
            setNameStatus(res.available ? 'available' : 'taken')
            if (!res.available) {
              setPlayerNameError('Player name is already taken')
            }
          }
          return curr
        })
      } catch {
        setNameStatus('error')
      }
    }, DEBOUNCE_MS)

    return () => clearTimeout(handle)
  }, [playerName, user?.displayName])

  function validate(): boolean {
    const trimmed = playerName.trim()
    if (!trimmed) {
      setPlayerNameError('Player name is required')
      return false
    }
    if (trimmed.length < USERNAME_MIN) {
      setPlayerNameError(`Must be at least ${USERNAME_MIN} characters`)
      return false
    }
    if (trimmed.length > USERNAME_MAX) {
      setPlayerNameError(`Must be ${USERNAME_MAX} characters or fewer`)
      return false
    }
    if (!USERNAME_RE.test(trimmed)) {
      setPlayerNameError('Letters, numbers, and underscores only')
      return false
    }
    if (nameStatus === 'taken') {
      setPlayerNameError('Player name is already taken')
      return false
    }
    setPlayerNameError('')
    return true
  }

  async function handleSave() {
    if (!validate()) return
    setSaving(true)
    try {
      await apiPatch<UserResponse>('/api/v1/users/me', { displayName: playerName.trim() })
      await refreshUser()
      setNameStatus('unchanged')
      showToast('Player name saved', 'success')
    } catch (err: unknown) {
      if (err instanceof HttpError && err.status === 400) {
        setPlayerNameError('Player name is already taken')
        setNameStatus('taken')
      } else {
        showToast(err instanceof Error ? err.message : 'Failed to save', 'error')
      }
    } finally {
      setSaving(false)
    }
  }

  const saveDisabled =
    !playerName.trim() ||
    !nameChanged ||
    saving ||
    nameStatus === 'checking' ||
    nameStatus === 'taken'

  const nameHint =
    !playerNameError && playerName.trim().length > 0
      ? nameStatus === 'checking'
        ? '// checking availability…'
        : nameStatus === 'available'
          ? `// "${playerName.trim()}" is available`
          : nameStatus === 'invalid'
            ? `// ${USERNAME_MIN}–${USERNAME_MAX} chars, letters / numbers / underscores`
            : nameStatus === 'error'
              ? '// could not check — try again'
              : undefined
      : undefined

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      {/* Header */}
      <View className="flex-row items-center px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOr(router, '/(app)/settings/account')}
          accessibilityLabel="Back to account settings"
          accessibilityRole="button"
          className="mr-3 p-1"
        >
          <Text className="text-blood text-base font-typed">← Back</Text>
        </TouchableOpacity>
        <Text className="text-ink font-stencil text-xl flex-1 uppercase">Player Name</Text>
      </View>

      <KeyboardAwareScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 16,
          gap: 20,
          alignItems: isLandscape ? 'center' : undefined,
        }}
        keyboardDismissMode="on-drag"
      >
          <View style={isLandscape ? { width: contentWidth } : { width: '100%' }}>
          <View className="bg-paper rounded-2xl p-4 gap-3">
            <Text className="text-ink font-stencil uppercase">Player Name</Text>
            <Input
              value={playerName}
              onChangeText={(v) => {
                setPlayerName(v)
                setPlayerNameError('')
              }}
              placeholder="Your player name"
              autoCapitalize="none"
              autoCorrect={false}
              maxLength={USERNAME_MAX}
              error={playerNameError}
            />
            {nameHint !== undefined && (
              <Text
                className={
                  nameStatus === 'available'
                    ? 'font-typed text-olive-up text-xs'
                    : 'font-typed text-ink-soft text-xs italic'
                }
                accessibilityLiveRegion="polite"
              >
                {nameHint}
              </Text>
            )}
            <Text className="font-typed text-ink-soft text-xs italic">
              // Letters, numbers, underscores. This is your @-handle too.
            </Text>
            <Button
              title="Save Changes"
              onPress={handleSave}
              loading={saving}
              disabled={saveDisabled}
            />
          </View>
          </View>
      </KeyboardAwareScrollView>
    </SafeAreaView>
  )
}
